/* Dates and times, formatted once and in one place.
 *
 * `i18n.md` bans formatting a date by hand and bans an implicit timezone, and
 * the second half is the one that bites: a time rendered in the machine's zone
 * is the most common bug class in event software, and it is invisible until
 * somebody in another country reads the screen.
 *
 * So `timeZone` is a REQUIRED argument. There is no default and there will not
 * be one — a default is what makes the wrong zone silent. Whose zone applies is
 * a decision per call site: an Event's time belongs to its Venue, an audit
 * timestamp to the reader.
 */
/** A date on its own: no time, so no zone is displayed. */
export function formatDate(at, { locale, timeZone }) {
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'long',
        timeZone,
    }).format(at);
}
/**
 * A date and a time, with the zone named.
 *
 * Added for STORY-012's audit trail, which is its first caller — this file
 * previously said there was deliberately no `formatDateTime` because nothing
 * used one, and `coding.md` rejects scaffolding written against a future need.
 *
 * **The zone is always displayed**, not merely applied. A reader seeing "19:00"
 * has no way to tell whose evening it is, and on a screen mixing an Event's
 * local time with their own that ambiguity is a missed event.
 *
 * Written with explicit component options rather than `dateStyle`/`timeStyle`
 * because `Intl` THROWS when `timeZoneName` is combined with either — which is
 * the whole reason this needed writing rather than one more line in the caller.
 */
export function formatDateTime(at, { locale, timeZone }) {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone,
    }).format(typeof at === 'string' ? new Date(at) : at);
}
//# sourceMappingURL=dates.js.map